import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Container, Row, Col, Card, CardBody, Button, Spinner, Alert } from "reactstrap";
import { fetchPublicSongFlow } from "../../components/helpers/PublicCuratedSongFlowHelpers";
import { debugLog } from "../../utils/debug";

function PublicSongFlow() {
    const { slug } = useParams();
    const navigate = useNavigate();

    const [songFlow, setSongFlow] = React.useState(null);
    const [isLoading, setIsLoading] = React.useState(true);
    const [notFound, setNotFound] = React.useState(false);

    React.useEffect(() => {
        setIsLoading(true);
        fetchPublicSongFlow(slug).then(data => {
            if (!data) {
                setNotFound(true);
            } else {
                setSongFlow(data);
                if (typeof window.gtag === "function") {
                    window.gtag("event", "public_song_flow_view", {
                        song_flow_slug: slug,
                        song_flow_name: data.name
                    });
                }
            }
            setIsLoading(false);
        });
    }, [slug]);

    const handleSubscribeClick = () => {
        if (typeof window.gtag === "function") {
            window.gtag("event", "public_song_flow_subscribe_start", { song_flow_slug: slug });
        }
        navigate(`/song-flows/${slug}/subscribe`);
    };

    const handleRegisterClick = () => {
        if (typeof window.gtag === "function") {
            window.gtag("event", "public_song_flow_register_click", { song_flow_slug: slug });
        }
    };

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
                <Spinner color="primary" />
            </div>
        );
    }

    if (notFound) {
        return (
            <div className="position-relative">
                <section className="section section-lg section-shaped pb-250">
                    <div className="shape shape-style-1 shape-default">
                        <span /><span /><span /><span /><span />
                    </div>
                    <Container className="py-lg-md d-flex">
                        <div className="col px-0">
                            <Row>
                                <Col lg="8">
                                    <h1 className="display-3 text-white">Song-flow not found</h1>
                                    <p className="lead text-white">
                                        This song-flow may have been unpublished or the link may be incorrect.
                                    </p>
                                </Col>
                            </Row>
                        </div>
                    </Container>
                    <div className="separator separator-bottom separator-skew">
                        <svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" viewBox="0 0 2560 100">
                            <polygon className="fill-white" points="2560 0 2560 100 0 100" />
                        </svg>
                    </div>
                </section>
            </div>
        );
    }

    const items = songFlow.items || [];

    return (
        <div className="position-relative">
            <section className="section section-lg section-shaped pb-250">
                <div className="shape shape-style-1 shape-default">
                    <span /><span /><span /><span /><span /><span /><span /><span /><span />
                </div>
                <Container className="py-lg-md d-flex">
                    <div className="col px-0">
                        <Row>
                            <Col lg="8">
                                <h1 className="display-3 text-white">{songFlow.name}</h1>
                                {songFlow.notes && (
                                    <p className="lead text-white">{songFlow.notes}</p>
                                )}
                            </Col>
                        </Row>
                    </div>
                </Container>
                <div className="separator separator-bottom separator-skew">
                    <svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" viewBox="0 0 2560 100">
                        <polygon className="fill-white" points="2560 0 2560 100 0 100" />
                    </svg>
                </div>
            </section>

            <section className="section section-lg pt-lg-0 mt--200">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg="10">
                            <Card className="shadow border-0">
                                <CardBody className="py-5 px-5">
                                    {items.length === 0 ? (
                                        <Alert color="light">This song-flow has no items yet.</Alert>
                                    ) : (
                                        <div>
                                            {items.map((item, idx) => (
                                                <div key={idx} style={{ marginBottom: "1rem" }}>
                                                    {item.itemType === "SERVICE_PART" ? (
                                                        <div style={{
                                                            padding: "14px 16px",
                                                            borderLeft: "5px solid #5e72e4",
                                                            background: "#f6f8ff",
                                                            borderRadius: "6px"
                                                        }}>
                                                            <div style={{ fontWeight: 700, fontSize: "1rem", color: "#172b4d" }}>
                                                                {item.servicePartTitle}
                                                            </div>
                                                            {item.servicePartNotes && (
                                                                <div style={{ marginTop: "6px", fontSize: "0.875rem", color: "#525f7f" }}>
                                                                    {item.servicePartNotes}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <Card className="shadow-sm border" style={{ borderRadius: "10px" }}>
                                                            <CardBody className="py-3 px-4">
                                                                <Row>
                                                                    <Col sm="7">
                                                                        <div style={{ fontWeight: 600, color: "#172b4d" }}>{item.songTitle}</div>
                                                                        {item.authors && item.authors.length > 0 && (
                                                                            <div style={{ fontSize: "0.8rem", color: "#525f7f" }}>
                                                                                {item.authors.map(a => a.name).join(", ")}
                                                                            </div>
                                                                        )}
                                                                    </Col>
                                                                    <Col sm="5" className="text-sm-right">
                                                                        <div style={{ fontSize: "0.85rem", color: "#525f7f" }}>{item.songBookTitle}</div>
                                                                        {item.songBookSongNumber && (
                                                                            <div style={{ fontSize: "0.8rem", color: "#adb5bd" }}>No. {item.songBookSongNumber}</div>
                                                                        )}
                                                                    </Col>
                                                                </Row>
                                                            </CardBody>
                                                        </Card>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <hr className="my-4" />

                                    <Row className="align-items-center">
                                        <Col md="7">
                                            <h5 className="mb-1">Receive curated song-flows by email</h5>
                                            <p className="text-muted mb-3" style={{ fontSize: "0.9rem" }}>
                                                Sign up free — no account required. Get worship song-flows delivered to your inbox.
                                            </p>
                                            <Button color="primary" onClick={handleSubscribeClick}>
                                                Subscribe free
                                            </Button>
                                        </Col>
                                        <Col md="5" className="text-md-right mt-4 mt-md-0">
                                            <h6 className="text-muted mb-1" style={{ fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                                Build your own
                                            </h6>
                                            <p className="text-muted mb-2" style={{ fontSize: "0.85rem" }}>
                                                Plan services around scripture with a full TWB account.
                                            </p>
                                            <Link
                                                to="/register/choose-your-plan"
                                                className="btn btn-outline-primary btn-sm"
                                                onClick={handleRegisterClick}
                                            >
                                                Register an account
                                            </Link>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </section>
        </div>
    );
}

export default PublicSongFlow;
